<script lang="ts">
  import { untrack } from 'svelte';
  import { api, ApiError } from '$shared/api';
  import PasswordInput from '$shared/PasswordInput.svelte';
  import { getCurrentRoute } from '$lib/router.svelte';

  let password = $state('');
  let confirmPassword = $state('');
  let success = $state(false);
  let error = $state('');
  let loading = $state(false);

  const route = $derived(getCurrentRoute());
  const token = untrack(() => new URLSearchParams(route.query).get('token') ?? '');

  async function handleSubmit(e: Event) {
    e.preventDefault();
    error = '';

    if (!token) {
      error = 'Invalid or missing reset token.';
      return;
    }

    if (password.length < 8) {
      error = 'Password must be at least 8 characters.';
      return;
    }

    if (password !== confirmPassword) {
      error = 'Passwords do not match.';
      return;
    }

    loading = true;
    try {
      await api.post('/auth/reset-password', { token, password });
      success = true;
    } catch (err) {
      console.error('Password reset failed:', err);
      error = (err as Error).message || 'Reset failed. The token may be invalid or expired.';
    }
    loading = false;
  }
</script>

<div class="reset-wrapper">
  <div class="reset-card">
    <h2>Set New Password</h2>
    {#if !token}
      <p class="msg--error">Invalid or missing reset token. Please request a new reset link.</p>
      <a href="#/forgot" class="btn btn-primary full-width">Request Reset Link</a>
    {:else if success}
      <p class="success-msg">Your password has been reset successfully.</p>
      <a href="#/login" class="btn btn-primary full-width">Go to Login</a>
    {:else}
      <form onsubmit={handleSubmit}>
        <PasswordInput
          id="reset-password"
          label="New Password"
          bind:value={password}
          required
          autocomplete="new-password"
          minlength={8}
        />
        <PasswordInput
          id="reset-password-confirm"
          label="Confirm Password"
          bind:value={confirmPassword}
          required
          autocomplete="new-password"
          minlength={8}
        />
        {#if error}<p class="msg--error">{error}</p>{/if}
        <button type="submit" class="btn btn-primary full-width" disabled={loading}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
      <p class="back-link"><a href="#/login">Back to Login</a></p>
    {/if}
  </div>
</div>

<style>
  .reset-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 2rem;
  }
  .reset-card {
    max-width: 400px;
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 2.5rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }
  .reset-card h2 {
    text-align: center;
    color: var(--text);
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.5rem;
    -webkit-text-fill-color: var(--text);
    background: none;
    background-clip: unset;
    -webkit-background-clip: unset;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .full-width {
    width: 100%;
    padding: 0.625rem;
  }
  .success-msg {
    color: var(--cyan);
    text-align: center;
    font-size: 0.9375rem;
  }
  .back-link {
    text-align: center;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }
  .back-link a {
    color: var(--cyan);
  }
</style>
